import fp from 'lodash/fp';
import qs from 'qs';
import axios, { AxiosResponse, AxiosRequestConfig, ResponseType } from 'axios';
import { decode as decodeBase64 } from 'universal-base64';
import { Pagination } from '@factorin/domain';

import { NotAuthorizedError, ForbiddenError, UnknownError, NotFoundError } from './errors';


export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'HEAD' | 'DELETE';

export type HttpHeaders = {
    [key: string]: string;
};

const mayHaveBody = (method: HttpMethod) =>
    ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());


export const contentTypeJson = 'application/json';
export const contentTypeErrorJson = 'application/problem+json';

export const checkContentType = (response: AxiosResponse, contentType: string) => fp.pipe(
    fp.defaultTo(''),
    fp.split('; '),
    fp.first,
    fp.defaultTo(''),
    fp.toLower,
    fp.isEqual(contentType),
)(response.headers['content-type']);


const extractUnicode = (fileName: string) => {
    const match = fileName.match(/^=\?utf-8\?B\?([^?]+)\?=$/);
    return !match ? fileName : fp.pipe(fp.get(1), decodeBase64)(match);
};

const extractFilename = (value: string) => {
    const match = value.match(/^filename=["']?([^'"]+)["']?$/);
    return match ? match[1] : '';
};

export const getFilename = (headers: any, fallback: string) => fp.pipe(
    fp.defaultTo(''),
    fp.split(';'),
    fp.map(fp.trim),
    fp.find(value => /^filename=/i.test(value)),
    fp.defaultTo(''),
    extractFilename,
    extractUnicode,
    value => value || fallback,
)(headers['content-disposition']);


export const rawRequest = async (request: AxiosRequestConfig) => {
    const response: AxiosResponse =
        await axios(request).catch(error => error.response);

    switch (response.status) {
        case 200:
        case 201:
        case 202:
        case 304:
        case 204:
            return response;

        case 401:
            throw new NotAuthorizedError(response);
        case 403:
            throw new ForbiddenError(response);
        case 404:
            throw new NotFoundError(response);

        case 400:
        case 500:
        default:
            throw new UnknownError(response);
    }
};

type Omit<TType, TExclude extends keyof TType> = Pick<TType, Exclude<keyof TType, TExclude>>;

export namespace Request {
    export type Url = {
        url: string;
    };

    export type ServerParameters = {
        basePath: string;
        apiPath: string;
    };

    export type Body<TBody = any> = {
        body: TBody;
    }

    export type Query<TQuery = object> = {
        query: TQuery;
    }

    export type Path = {
        path: string;
    }

    export type Cancel = {
        setCancel?: (cancel: () => void) => void;
    }

    export type UploadProgress = {
        onUploadProgress?: (progressEvent: any) => void;
    }

    export type DownloadProgress = {
        onDownloadProgress?: (progressEvent: any) => void;
    }

    export type Parameters =
        ServerParameters &
        Url &
        Partial<Path> &
        Partial<Body> &
        Partial<Query> &
        UploadProgress &
        DownloadProgress &
        Cancel &
        {
            method: HttpMethod;
            headers?: HttpHeaders;
            responseType?: ResponseType;
            contentType?: string;
        };
}


export const request = async <TResult = any>({
    basePath,
    apiPath,
    url,
    path = '',
    query = {},
    method,
    headers = {},
    responseType,
    contentType = contentTypeJson,
    onDownloadProgress,
    onUploadProgress,
    body,
    setCancel,
}: Request.Parameters): Promise<AxiosResponse<TResult>> => {
    if (!url)
        throw new Error('Url is not specified');

    const hasBody = body !== undefined;

    if (hasBody && !mayHaveBody(method))
        throw new Error('Incorrect body or method usage');

    const requestQuery = qs.stringify(query, { arrayFormat: 'repeat' });

    const requestUrl = fp.pipe(
        fp.filter(fp.identity),
        fp.join(''),
    )([
        basePath,
        apiPath,
        '/',
        url,
        path && '/',
        path,
        requestQuery && '?',
        requestQuery,
    ]);

    const requestParameters: AxiosRequestConfig = {
        url: requestUrl,
        method,
        headers: {
            Accept: contentTypeJson,
            ...hasBody && { 'Content-Type': contentType },
            ...headers,
        },
        ...hasBody && {
            data: contentType === contentTypeJson
                ? JSON.stringify(body)
                : body,
        },
        responseType,
        onDownloadProgress,
        onUploadProgress,
        ...setCancel && {
            cancelToken: new axios.CancelToken(setCancel),
        },
    };

    return rawRequest(requestParameters);
};

type MethodParameters =
    Omit<Request.Parameters, keyof Request.ServerParameters | 'method'>;

type RequestTransformArgs<TArgs> = (
    (TArgs extends void ? {} : TArgs) &
    Request.ServerParameters & Partial<MethodParameters>
);

type RequestTransform<TArgs> = TArgs extends void
    ? () => MethodParameters
    : (args: TArgs) => MethodParameters;

const method = (name: HttpMethod) =>
    <TArgs, TResult = any>(transform: RequestTransform<TArgs>) =>
        (args: RequestTransformArgs<TArgs>) => request<TResult>({
            ...args,
            ...transform(args as TArgs),
            method: name,
        });

export const get = method('GET');
export const post = method('POST');
export const put = method('PUT');
export const patch = method('PATCH');
export const remove = method('DELETE');

type WithUrlMapper = (args?: Partial<Omit<MethodParameters, 'url'>>) => MethodParameters;

export const withUrl = (url: string): WithUrlMapper => (args = {}) => ({ ...args, url });

export type ItemRequest = {
    id: string | number;
};

export type ItemUpdate<TItem> = {
    id: string | number;
    item: TItem;
}

export const list = <
    TItem = any,
    TRequest extends object = object,
    TList = TItem,
>({ url }: Request.Url) => ({
    item: get<ItemRequest, TItem>(
        ({ id }) => ({
            url,
            path: `${id}`,
        }),
    ),
    list: get<Request.Query<TRequest>, Pagination.List<TList>>(
        ({ query }) => ({
            url,
            query,
        }),
    ),
});

export const readUpdate = <
    TItem = any,
>({ url }: Request.Url) => ({
    item: get<ItemRequest, TItem>(
        ({ id }) => ({
            url,
            path: `${id}`,
        }),
    ),
    update: put<ItemUpdate<TItem>, TItem>(
        ({ id, item }) => ({
            url,
            path: `${id}`,
            body: item,
        }),
    ),
});

type ApiCall<TArgs, TResult = any> =
    (args: RequestTransformArgs<TArgs>) => Promise<AxiosResponse<TResult>>;

type ExtractApiArgType<TApiCall> = TApiCall extends ApiCall<infer TArgs> ? TArgs : never;
type ExtractApiResult<TApiCall> = TApiCall extends ApiCall<{}, infer TResult> ? TResult : any;

export type AxiosResponseType<TType> = AxiosResponse<TType>;
export type ApiCaller = <TApiCall extends ApiCall<{}>>(
    apiCall: TApiCall,
    args?: ExtractApiArgType<TApiCall>
) => Promise<AxiosResponse<ExtractApiResult<TApiCall>>>;


type ApiCallerParameters = Request.ServerParameters & Partial<MethodParameters>;
type ApiCallerCreator = {
    (params: ApiCallerParameters): ApiCaller;
    (params: () => ApiCallerParameters): ApiCaller;
};

type ObjectOrCreator<TObject extends {}> = TObject | ((args: any) => TObject);

export const createApiCaller: ApiCallerCreator =
    (params: ObjectOrCreator<ApiCallerParameters>): ApiCaller =>
        (apiCall, args) => apiCall({
            ...args,
            ...fp.isFunction(params)
                ? params(args)
                : params,
        });
